import { Loader2, RefreshCw, BarChart3, ChefHat, Clock, Activity, Package, TrendingUp } from "lucide-react";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { LockedFeature } from "@/components/shared/LockedFeature";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { useKitchenAnalytics } from "@/hooks/useKitchenAnalytics";
import { EfficiencyScoreCard } from "@/components/kitchen-analytics/EfficiencyScoreCard";
import { OperationStatusCard } from "@/components/kitchen-analytics/OperationStatusCard";
import { AIAnalysis } from "@/components/kitchen-analytics/AIAnalysis";
import { BenchmarkCard } from "@/components/kitchen-analytics/BenchmarkCard";
import { ProductTimesCard } from "@/components/kitchen-analytics/ProductTimesCard";
import { HeatmapCard } from "@/components/kitchen-analytics/HeatmapCard";
import { fmt } from "@/components/kitchen-analytics/analyticsUtils";

export default function KitchenAnalyticsPage() {
  const gate = useFeatureGate("kitchen_analytics");
  const {
    kitchen, heatmap, bottlenecks, efficiency, benchmark,
    loading, analyzing, days, setDays,
    fetchAll, handleAnalyze,
  } = useKitchenAnalytics();

  if (gate === "blocked") return <LockedFeature featureName="Analytics Cozinha" addonId="kitchen_analytics" />;

  if (loading && !kitchen) {
    return (
      <div className="flex justify-center items-center h-64 bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const km = kitchen || {};
  const bn = bottlenecks || {};
  const ef = efficiency || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kitchen-analytics-page">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-xl">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">Análises & Relatórios</h1>
                <p className="text-sm text-gray-500">Inteligência operacional do restaurante</p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Select value={days} onValueChange={setDays} data-testid="period-select">
                <SelectTrigger className="w-full sm:w-[140px] bg-gray-50 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="14">Últimos 14 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="60">Últimos 60 dias</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchAll} className="bg-white" data-testid="refresh-analytics">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Atualizar
              </Button>
            </div>
          </div>
        </div>

        {/* Efficiency + Operation Status */}
        <div className="grid lg:grid-cols-3 gap-6">
          <EfficiencyScoreCard efficiency={ef} />
          <OperationStatusCard bottlenecks={bn} />
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white shadow-sm" data-testid="metric-prep-time">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-xl"><ChefHat className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Preparo (p50)</p>
                  <p className="text-2xl font-bold text-gray-900">{fmt(km.prep_time?.p50)}</p>
                  <p className="text-xs text-gray-400">p95: {fmt(km.prep_time?.p95)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm" data-testid="metric-queue-time">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl"><Clock className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Fila (p50)</p>
                  <p className="text-2xl font-bold text-gray-900">{fmt(km.queue_time?.p50)}</p>
                  <p className="text-xs text-gray-400">p95: {fmt(km.queue_time?.p95)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm" data-testid="metric-total-time">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl"><Activity className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Total (p50)</p>
                  <p className="text-2xl font-bold text-gray-900">{fmt(km.total_time?.p50)}</p>
                  <p className="text-xs text-gray-400">p95: {fmt(km.total_time?.p95)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm" data-testid="metric-orders">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl"><Package className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Pedidos ({days}d)</p>
                  <p className="text-2xl font-bold text-gray-900">{km.total_orders || 0}</p>
                  <p className="text-xs text-gray-400">Cancelados: {km.cancelled || 0} ({km.cancel_rate_pct || 0}%)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Analysis + Benchmark + Product Times */}
        <div className="grid lg:grid-cols-3 gap-6">
          <AIAnalysis efficiency={ef} bottlenecks={bn} kitchen={km} onAnalyze={handleAnalyze} analyzing={analyzing} />
          <BenchmarkCard benchmark={benchmark} />
          <ProductTimesCard kitchen={km} />
        </div>

        <HeatmapCard heatmap={heatmap} />

        {/* Source Distribution */}
        {Object.keys(km.source_distribution || {}).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6" data-testid="source-distribution">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              <h2 className="text-base font-semibold text-gray-800">Distribuição por Canal</h2>
            </div>
            <div className="flex gap-3 flex-wrap">
              {Object.entries(km.source_distribution).map(([src, count]) => (
                <div key={src} className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-xl border border-gray-100">
                  <span className="text-xl font-bold text-gray-900">{count}</span>
                  <span className="text-sm text-gray-600 capitalize">{src}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
